import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { DataFlag, ForecastedUsageApiResponse, Graph, GraphPair, TenantUsageData } from 'src/app/app.types';
import { UsageAmountsService } from 'src/app/services/usage/usage-amounts.service';
import { convert, convertDateToMonth, convertMonthKeyToDate } from 'src/util/conversion-helpers';

type DataShownOption = 'storage' | 'incidents';

@Component({
    selector: 'incidents-created-chart',
    templateUrl: './incidents-created-chart.component.html',
    styleUrls: ['./incidents-created-chart.component.scss'],
    standalone: false
})
export class IncidentsCreatedChartComponent implements OnInit, OnDestroy {
  @Input() tenantId?: string;

  endOfFirstRetention: Date | null = null;
  _initialTenantData?: TenantUsageData;

  earliestSelectionDate: Date = new Date(2024, 0, 1);
  latestSelectionDate: Date = new Date(2030, 11, 1);
  titles: { [key in DataShownOption]: string } = {
    storage: 'Storage used in GB over time',
    incidents: 'Incidents created over time'
  };

  dataShown: DataShownOption = 'incidents';

  public get subStartStr() : string {
    if(this._initialTenantData?.customer?.subscriptionStartDate){
      return convertDateToMonth(new Date(this._initialTenantData.customer.subscriptionStartDate));
    }
    return "";
  }
  
  public get retentionEndStr() : string {
    if(this.endOfFirstRetention){
      return convertDateToMonth(this.endOfFirstRetention);
    }
    return "";
  }
  
  public get currentTitle() : string {
    return this.titles[this.dataShown];
  }
  
  @Input()
  public set selectedDate(v : Date) {
    this.graphPair.startDate = v;
    this.refreshDataForStartDate$.next(v);
  }
  
  public get selectedDate() : Date | undefined {
    return this.graphPair.startDate;
  }

  @Input()
  public set initialTenantData(v : TenantUsageData) {
    console.log('Initial tenant data set:', v);
    this._initialTenantData = v;

    if(v) {
      this.endOfFirstRetention = v.endOfFirstRetention;
      this.graphPair.startDate = Object.keys(v.usage).length > 0 ? convertMonthKeyToDate(Object.keys(v.usage)[0]) : this.graphPair.startDate;
      this.earliestSelectionDate = this.graphPair.startDate;
      if(v.customer?.subscriptionStartDate && new Date(v.customer?.subscriptionStartDate).getTime() < this.earliestSelectionDate.getTime()) {
        const subStartDate = new Date(v.customer.subscriptionStartDate);
        this.earliestSelectionDate = new Date(subStartDate.getFullYear(), subStartDate.getMonth(), 1);
      }
      this.setDataPointsFromUsageData(v.usage);
    }
  }

  dataFlagMap: Map<number, DataFlag> = new Map<number, DataFlag>();
  
  graphPair: GraphPair = {
    leftGraph: { description: 'Incidents Added', type: 'bar', color: '#FC9081', data: [], labelType: 'normal' },
    rightGraph: { description: 'Incidents total count', type: 'line', color: '#AFE890', data: [], labelType: 'hover' },
    startDate: new Date(new Date().getFullYear()-1, new Date().getMonth() + 1, 1)
  }

  incidentsCreatedGraph: Graph = { description: 'Incidents Added', type: 'bar', color: '#FC9081', data: [], labelType: 'normal' };
  totalActiveIncidentsGraph: Graph = { description: 'Incidents total count', type: 'line', color: '#AFE890', data: [], labelType: 'hover' };

  monthlyStorageGraph: Graph = { description: 'Monthly Storage Increase', type: 'line', color: '#1B82CB', data: [], labelType: 'normal' };
  totalStorageGraph: Graph = { description: 'Total Storage', type: 'line', color: '#AFE890', data: [], labelType: 'hover' };

  refreshDataForTenant$ = new Subject<string>();
  refreshDataForStartDate$ = new Subject<Date>();
  unSubscribe$ = new Subject();

  constructor(private readonly usageService: UsageAmountsService) {}

  ngOnDestroy(): void {
    this.unSubscribe$.next(null);
    this.unSubscribe$.complete();
  }

  ngOnInit(): void {
    this.refreshDataForStartDate$.pipe(
      filter(() => !!this.tenantId),
      switchMap((startDate) => this.usageService.getForecast(
        this.tenantId, startDate.toISOString(), 12, this.endOfFirstRetention
      )),
      takeUntil(this.unSubscribe$)
    ).subscribe(usageData => {
        console.log(`Usage data for ${this.tenantId} :`, usageData);
        this.setDataPointsFromUsageData(usageData);
    });
  }

  onDateChange(selectedDate: Date) {
    this.selectedDate = selectedDate;
  }

  setDataShown(value: DataShownOption | null = null) {
    if(value) {this.dataShown = value;}

    if(this.dataShown === 'incidents'){
      this.graphPair = {
        leftGraph: {...this.incidentsCreatedGraph},
        rightGraph: {...this.totalActiveIncidentsGraph},
        startDate: this.graphPair.startDate
      }
    } else {
      this.graphPair = {
        leftGraph: {...this.monthlyStorageGraph},
        rightGraph: {...this.totalStorageGraph},
        startDate: this.graphPair.startDate
      }
    }

    this.dataFlagMap = this.createDataFlagMap(this.graphPair.startDate);
  }

  setDataPointsFromUsageData(usageData: ForecastedUsageApiResponse) {
    this.incidentsCreatedGraph.data = Object.entries(usageData).map(u => { 
      return {
        name: convertDateToMonth(convertMonthKeyToDate(u[0])), actual: u[1].actualUsage?.incidentsCreatedLastHourCount, predicted: u[1].forecastedUsage?.incidentsCreatedLastHourCount
      }
    });
    this.totalActiveIncidentsGraph.data = Object.entries(usageData)
      .filter(u => u[1].actualUsage?.activeIncidents || u[1].forecastedUsage?.activeIncidents).map(u => { 
        return {
          name: convertDateToMonth(convertMonthKeyToDate(u[0])), actual: u[1].actualUsage?.activeIncidents, predicted: u[1].forecastedUsage?.activeIncidents
        }
    });
    
    this.monthlyStorageGraph.data = Object.entries(usageData)
      .filter(u => u[1].actualUsage?.storageIncreaseInTimeFrame || u[1].forecastedUsage?.storageIncreaseInTimeFrame).map(u => { 
        return {
          name: convertDateToMonth(convertMonthKeyToDate(u[0])), 
          actual: convert("bytes", "gigabytes", u[1].actualUsage?.storageIncreaseInTimeFrame, 1), 
          predicted: convert("bytes", "gigabytes",   u[1].forecastedUsage?.storageIncreaseInTimeFrame, 1) ?? undefined
        }
    });
    this.totalStorageGraph.data = Object.entries(usageData).map(u => { 
      return {
        name: convertDateToMonth(convertMonthKeyToDate(u[0])), 
        actual: convert("bytes", "gigabytes", u[1].actualUsage?.totalStorageUsage, 1), 
        predicted: convert("bytes", "gigabytes", u[1].forecastedUsage?.totalStorageUsage, 1) ?? undefined
      }
    }); 

    console.log("monthlyStorageGraph data:", this.monthlyStorageGraph.data);
    console.log("totalStorageGraph data:", this.totalStorageGraph.data);

    this.setDataShown();
  }

  createDataFlagMap(startDate: Date): Map<number, DataFlag> {
    const flagMap = new Map<number, DataFlag>();
    const currentDate = new Date(startDate.getTime());
    let monthCounter = 0;

    do {
      if(this._initialTenantData?.customer?.subscriptionStartDate && 
        new Date(this._initialTenantData.customer.subscriptionStartDate).getFullYear() === currentDate.getFullYear() && 
        new Date(this._initialTenantData.customer.subscriptionStartDate).getMonth() === currentDate.getMonth()){
          flagMap.set(monthCounter, { icon: 'start_region', tooltip: 'Start Tenancy', color: '#14AE5C', showLeftOfLabel: true});
      }
      else if(this.endOfFirstRetention && 
        this.endOfFirstRetention.getFullYear() === currentDate.getFullYear() && 
        this.endOfFirstRetention.getMonth() === currentDate.getMonth()){
          flagMap.set(monthCounter, { icon: 'end_region', tooltip: 'End of Retention', color: '#FF6861', showLeftOfLabel: false});
      }
      currentDate.setMonth(currentDate.getMonth() + 1);
      monthCounter++;
    } while (currentDate.getMonth() !== startDate.getMonth());

    return flagMap;
  }
}
